"use client";

import { useEffect, useRef } from "react";

type UseRevealOptions = {
  threshold?: number;
  rootMargin?: string;
  selector?: string;
  staggerInterval?: number;
  once?: boolean;
  onReveal?: () => void;
};

type ObserverEntry = {
  observer: IntersectionObserver;
  callbacks: WeakMap<Element, (entry: IntersectionObserverEntry) => void>;
};

const observers = new Map<string, ObserverEntry>();

function getObserver({
  threshold = 0.1,
  rootMargin = "0px",
}: Pick<UseRevealOptions, "threshold" | "rootMargin">): ObserverEntry {
  const key = `${threshold}-${rootMargin}`;
  let entry = observers.get(key);

  if (!entry) {
    const callbacks = new WeakMap<
      Element,
      (entry: IntersectionObserverEntry) => void
    >();
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((observerEntry) => {
          const callback = callbacks.get(observerEntry.target);
          if (callback) {
            callback(observerEntry);
          }
        });
      },
      { threshold, rootMargin },
    );

    entry = { observer, callbacks };
    observers.set(key, entry);
  }

  return entry;
}

export function useRevealOnScroll<T extends HTMLElement>(
  options: UseRevealOptions = {},
) {
  const {
    selector = ".reveal",
    staggerInterval = 150,
    once = true,
    onReveal,
    threshold,
    rootMargin,
  } = options;
  const containerRef = useRef<T | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const revealElements = Array.from(
      container.querySelectorAll<HTMLElement>(selector),
    );
    if (revealElements.length === 0) return;

    const timeouts: number[] = [];

    const observerEntry = getObserver({
      threshold,
      rootMargin,
    });

    const handleIntersect = (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting) return;

      onReveal?.();

      revealElements.forEach((element, index) => {
        const timeoutId = window.setTimeout(() => {
          element.classList.add("active");
        }, index * staggerInterval);
        timeouts.push(timeoutId);
      });

      if (once) {
        observerEntry.observer.unobserve(entry.target);
      }
    };

    observerEntry.callbacks.set(container, handleIntersect);
    observerEntry.observer.observe(container);

    return () => {
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      observerEntry.callbacks.delete(container);
      observerEntry.observer.unobserve(container);
    };
  }, [threshold, rootMargin, selector, staggerInterval, once, onReveal]);

  return containerRef;
}
